/* =============================================================================
 * posthog-events.js
 *
 * Cart events + PDP Quantity Changed.
 *
 * Loaded once in theme.liquid <head>, after posthog-utils.js:
 *   <script src="{{ 'posthog-events.js' | asset_url }}" defer></script>
 *
 * Events fired:
 *   - Cart Viewed              (drawer open OR /cart page load)
 *   - Cart Item Removed        (remove button click)
 *   - Cart Quantity Changed    (qty input change in cart)
 *   - Discount Code Attempted  (discount form submit)
 *   - Cart Abandoned           (synthetic — visibility-hidden with non-empty cart)
 *   - Quantity Changed         (PDP qty input change)
 *
 * Defensive design: every listener body wraps in try/catch.
 * window.safeCapture (from posthog-utils.js) also wraps. Belt-and-suspenders.
 *
 * Deliberately NOT included today (theme-selector risk — defer until DOM inspectable):
 *   - Variant Selected
 *   - Subscription Toggled (Appstle)
 *   - Image Carousel Advanced
 *   - Reviews Section Viewed / Review Expanded
 *   - FAQ Opened
 * ============================================================================= */

(function () {
  'use strict';

  // Wait for DOM ready before binding listeners
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  // ---------------------------------------------------------------------------
  // Helper: fetch current cart state via Shopify Cart API
  // ---------------------------------------------------------------------------
  async function fetchCart() {
    try {
      var resp = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!resp.ok) return null;
      return await resp.json();
    } catch (e) {
      return null;
    }
  }

  function summarizeCart(cart) {
    if (!cart) return { cart_total: null, cart_count: 0, items: [] };
    return {
      cart_total: cart.total_price ? cart.total_price / 100 : 0,
      cart_count: cart.item_count || 0,
      currency: cart.currency || 'USD',
      items: (cart.items || []).map(function (i) {
        return {
          product_id: i.product_id,
          variant_id: i.variant_id,
          qty: i.quantity,
          price: i.price ? i.price / 100 : 0,
          name: i.product_title,
          handle: i.handle
        };
      })
    };
  }

  // ---------------------------------------------------------------------------
  // 1. Cart Viewed — drawer open OR /cart page load
  // ---------------------------------------------------------------------------
  async function captureCartViewed(viewType) {
    try {
      var cart = await fetchCart();
      var summary = summarizeCart(cart);
      window.safeCapture('Cart Viewed', Object.assign({ view_type: viewType }, summary));
    } catch (e) {
      // never let cart capture break a page interaction
    }
  }

  function bindCartViewed() {
    // /cart page
    try {
      if (window.location.pathname === '/cart') {
        captureCartViewed('page');
      }
    } catch (e) {}

    // Cart drawer open — themes vary; listen for common custom events first
    try {
      ['cart:open', 'cart-drawer:open', 'drawerOpen:cart'].forEach(function (evt) {
        document.addEventListener(evt, function () { captureCartViewed('drawer'); });
      });
    } catch (e) {}

    // Fallback: observe class changes on a likely cart drawer element
    try {
      var drawerSelectors = [
        'cart-drawer', 'cart-notification', '.cart-drawer', '.drawer--cart',
        '[data-cart-drawer]', '#cart-drawer'
      ];
      drawerSelectors.forEach(function (sel) {
        var el = document.querySelector(sel);
        if (!el) return;
        var lastOpen = el.classList.contains('is-open') || el.classList.contains('active') || el.hasAttribute('open');
        new MutationObserver(function (mutations) {
          try {
            var nowOpen = el.classList.contains('is-open') || el.classList.contains('active') || el.hasAttribute('open');
            if (nowOpen && !lastOpen) captureCartViewed('drawer');
            lastOpen = nowOpen;
          } catch (e) {}
        }).observe(el, { attributes: true, attributeFilter: ['class', 'open', 'aria-hidden'] });
      });
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // 2. Cart Item Removed
  // ---------------------------------------------------------------------------
  function bindCartItemRemoved() {
    document.addEventListener('click', function (e) {
      try {
        var btn = e.target && e.target.closest && e.target.closest(
          '[data-cart-remove], cart-remove-button, .cart-item__remove, ' +
          'a[href*="/cart/change?"][href*="quantity=0"], button[name="remove"]'
        );
        if (!btn) return;
        window.safeCapture('Cart Item Removed', {
          product_id: btn.dataset.productId || null,
          variant_id: btn.dataset.variantId || btn.dataset.id || null,
          line_item: btn.dataset.lineItem || null,
          source: 'cart_remove_click'
        });
      } catch (err) {}
    });
  }

  // ---------------------------------------------------------------------------
  // 3. Cart Quantity Changed (cart page / drawer)
  // ---------------------------------------------------------------------------
  function bindCartQuantityChanged() {
    document.addEventListener('change', function (e) {
      try {
        var input = e.target;
        if (!input || input.tagName !== 'INPUT') return;
        // Cart qty inputs are typically: name="updates[<id>]" or in <cart-items>
        var isCartQty = input.name && /updates\[/.test(input.name);
        var inCartItems = input.closest && input.closest('cart-items, .cart-items, [data-cart-items]');
        if (!isCartQty && !inCartItems) return;

        var fromQty = parseInt(input.dataset.previousValue || input.defaultValue || '1', 10);
        var toQty = parseInt(input.value, 10);
        if (isNaN(toQty)) return;

        window.safeCapture('Cart Quantity Changed', {
          product_id: input.dataset.productId || null,
          variant_id: input.dataset.variantId || input.dataset.id || null,
          from_qty: fromQty,
          to_qty: toQty,
          delta: toQty - fromQty
        });
        input.dataset.previousValue = String(toQty);
      } catch (err) {}
    });
  }

  // ---------------------------------------------------------------------------
  // 4. Discount Code Attempted
  // ---------------------------------------------------------------------------
  function bindDiscountCodeAttempted() {
    document.addEventListener('submit', function (e) {
      try {
        var form = e.target;
        if (!form || !form.matches) return;
        var isDiscountForm = form.matches(
          'form[action*="/discount/"], form[action*="discount_code"], [data-discount-form]'
        );
        if (!isDiscountForm) return;
        var codeInput = form.querySelector('input[name="discount"], input[name="discount_code"], input[type="text"]');
        var code = (codeInput && codeInput.value || '').trim();
        if (!code) return;
        window.safeCapture('Discount Code Attempted', {
          code: code.toUpperCase()
        });
      } catch (err) {}
    });
  }

  // ---------------------------------------------------------------------------
  // 5. Cart Abandoned (synthetic)
  // Fires on visibility-hidden when cart has items and no checkout was started.
  // ---------------------------------------------------------------------------
  var checkoutStarted = false;
  var cartAbandonedFired = false;

  function bindCheckoutStartedTracking() {
    // Mark checkout intent on common signals
    try {
      document.addEventListener('click', function (e) {
        try {
          var btn = e.target && e.target.closest && e.target.closest(
            'button[name="checkout"], a[href^="/checkout"], a[href*="/checkouts/"], ' +
            'input[name="checkout"], [data-checkout-button]'
          );
          if (btn) checkoutStarted = true;
        } catch (err) {}
      });
      document.addEventListener('submit', function (e) {
        try {
          var form = e.target;
          if (form && form.action && /\/(cart|checkout)/.test(form.action)) {
            checkoutStarted = true;
          }
        } catch (err) {}
      });
    } catch (e) {}
  }

  function bindCartAbandoned() {
    document.addEventListener('visibilitychange', function () {
      try {
        if (document.visibilityState !== 'hidden') return;
        if (checkoutStarted || cartAbandonedFired) return;
        fetchCart().then(function (cart) {
          try {
            if (!cart || !cart.item_count || cart.item_count === 0) return;
            if (cartAbandonedFired) return;
            cartAbandonedFired = true;
            var summary = summarizeCart(cart);
            // Use sendBeacon-style; posthog handles its own batching on unload
            window.safeCapture('Cart Abandoned', Object.assign({
              seconds_in_session: Math.round(performance.now() / 1000)
            }, summary));
          } catch (err) {}
        });
      } catch (err) {}
    });
  }

  // ---------------------------------------------------------------------------
  // 6. PDP Quantity Changed
  // Selector-stable: input[name="quantity"] is Shopify-standard
  // ---------------------------------------------------------------------------
  function bindPdpQuantityChanged() {
    document.addEventListener('change', function (e) {
      try {
        var input = e.target;
        if (!input || input.tagName !== 'INPUT') return;
        // Match PDP qty input but exclude cart updates[] inputs
        if (input.name !== 'quantity') return;
        if (/updates\[/.test(input.name)) return;
        // Must be on a product page (not cart)
        if (window.location.pathname.indexOf('/products/') === -1) return;

        var fromQty = parseInt(input.dataset.previousValue || input.defaultValue || '1', 10);
        var toQty = parseInt(input.value, 10);
        if (isNaN(toQty)) return;

        window.safeCapture('Quantity Changed', {
          from_qty: fromQty,
          to_qty: toQty,
          delta: toQty - fromQty,
          product_id: input.dataset.productId || null,
          variant_id: input.dataset.variantId || null,
          pathname: window.location.pathname
        });
        input.dataset.previousValue = String(toQty);
      } catch (err) {}
    });
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------
  onReady(function () {
    try {
      bindCartViewed();
      bindCartItemRemoved();
      bindCartQuantityChanged();
      bindDiscountCodeAttempted();
      bindCheckoutStartedTracking();
      bindCartAbandoned();
      bindPdpQuantityChanged();
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('[posthog-events] boot failed:', e);
      }
    }
  });
})();
